/**
 * HeaderLogo.
 *
 * Renders the office logo from one of two theme-specific PNGs:
 *   /logo_dark_mode.png  -- white outline, used when the active theme is "dark"
 *   /logo_light_mode.png -- black outline, used when the active theme is "light"
 * Vite copies anything under public/ verbatim to the build root, so both
 * paths resolve identically in dev and prod.
 *
 * Missing-asset behavior: if the active image returns 404, onError fires
 * and we render a small dashed-bordered placeholder so the header layout
 * does not shift. The failed flag resets via useEffect whenever src changes,
 * so a 404 on one theme's image does not permanently block the other theme's
 * image. useEffect is simpler here than an inner-component key trick because
 * failed lives in this component's state; resetting it explicitly on src
 * change is one line and makes the intent obvious.
 *
 * Operator note: to update the logo, replace the corresponding PNG in
 * apps/web/public/ and refresh. No code change required.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useThemeStore } from "@/stores/themeStore";

export function HeaderLogo() {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const src = theme === "dark" ? "/logo_dark_mode.png" : "/logo_light_mode.png";
  const [failed, setFailed] = useState(false);

  // Reset on src change so each theme's image gets its own load attempt.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (failed) {
    return (
      <div
        aria-label={t("app.officeNameShort")}
        title={t("app.officeNameShort")}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-muted dark:border-muted-dark text-[10px] font-medium uppercase tracking-wide text-muted dark:text-muted-dark"
      >
        Logo
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={t("app.officeNameShort")}
      onError={() => setFailed(true)}
      // 40 px square -- fits the existing 56 px header (h-14) with
      // comfortable vertical padding. object-contain preserves the
      // operator's PNG aspect ratio without cropping.
      className="h-10 w-10 shrink-0 object-contain"
    />
  );
}
